import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  TextField,
  Autocomplete,
} from "@mui/material";

import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import OutlinedButton from "../../../components/pages/_new/super/common/OutlinedButton";
import colors from "../../../utils/colors";
import { SmartParseSensitivity, WafAction } from "../../../utils/constants";

function OrgUpdateModal({ open, onClose, onSubmit, organisations, loading }) {
  const [selectedOrg, setSelectedOrg] = React.useState(null);
  const [enabled, setEnabled] = React.useState(true);
  const [sensitivity, setSensitivity] = React.useState(SmartParseSensitivity.MEDIUM);
  const [action, setAction] = React.useState(WafAction.BLOCK);

  const handleSubmit = () => {
    if (!selectedOrg) return;
    onSubmit(selectedOrg.id, {
      smart_parse_module_active: enabled,
      smart_parse_sensitivity: sensitivity,
      waf_action_smart_parse: action,
    });
  };

  const labelSx = {
    fontSize: 14,
    fontWeight: 500,
    color: colors.neutral[700],
    mb: 1,
  };

  const selectSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: 14,
    },
    "& .MuiSelect-select": {
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <DialogTitle sx={ { fontSize: 20, fontWeight: 600, color: colors.neutral[900], pb: 1 }}>
        Update SmartParse by Organisation
      </DialogTitle>
      <DialogContent>
        <Typography sx={ { fontSize: 14, color: colors.neutral[600], mb: 3 }}>
          Update SmartParse WAF settings for all sites belonging to an organisation.
        </Typography>

        <Stack spacing={3}>
          <Box>
            <Typography sx={labelSx}>Select Organisation</Typography>
            <Autocomplete
              options={organisations || []}
              getOptionLabel={(option) => option.title || option.id || ""}
              value={selectedOrg}
              onChange={(event, newValue) => setSelectedOrg(newValue)}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search organisation..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: 14,
                    },
                  }}
                />
              )}
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: colors.primary[300],
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: colors.primary[300],
                    },
                  }}
                />
              }
              label={
                <Typography sx={ { fontSize: 14, fontWeight: 500, color: colors.neutral[700] }}>
                  Enable SmartParse WAF
                </Typography>
              }
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>Sensitivity Level</Typography>
            <FormControl fullWidth sx={selectSx}>
              <Select value={sensitivity} onChange={(e) => setSensitivity(e.target.value)} disabled={!enabled}>
                <MenuItem value={SmartParseSensitivity.LOW}>Low - Fewer detections, lower false positives</MenuItem>
                <MenuItem value={SmartParseSensitivity.MEDIUM}>Medium - Balanced detection (Recommended)</MenuItem>
                <MenuItem value={SmartParseSensitivity.HIGH}>High - More detections, higher false positives</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography sx={labelSx}>WAF Action</Typography>
            <FormControl fullWidth sx={selectSx}>
              <Select value={action} onChange={(e) => setAction(e.target.value)} disabled={!enabled}>
                <MenuItem value={WafAction.DETECT}>Detect - Log only, don't block</MenuItem>
                <MenuItem value={WafAction.BLOCK}>Block - Block malicious requests</MenuItem>
                <MenuItem value={WafAction.CHALLENGE}>Challenge - Present CAPTCHA challenge</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
        <PrimaryButton onClick={handleSubmit} disabled={!selectedOrg}>
          Update Organisation
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

export default OrgUpdateModal;
